import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'sonner';
import { contactKeys, dealKeys, leadKeys } from '@/lib/query-keys';
import { contactService } from '@/services/contact.service';
import { useContactFilterStore } from '@/stores/contactFilterStore';

export function useContacts() {
  const queryClient = useQueryClient();
  const filters = useContactFilterStore();

  const contactsQuery = useQuery({
    queryKey: contactKeys.list(filters.current),
    queryFn: () => contactService.getAll(filters.current),
    placeholderData: keepPreviousData,
  });

  const createContact = useMutation({
    mutationFn: (data: Parameters<typeof contactService.create>[0]) => contactService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contactKeys.lists() });
      toast.success('Contact created successfully');
    },
    onError: () => toast.error('Failed to create contact'),
  });

  const updateContact = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof contactService.update>[1] }) =>
      contactService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: contactKeys.lists() });
      queryClient.invalidateQueries({ queryKey: contactKeys.detail(id) });
      toast.success('Contact updated successfully');
    },
    onError: () => toast.error('Failed to update contact'),
  });

  const deleteContact = useMutation({
    mutationFn: ({ id, deleteDeals, deleteLead }: { id: string; deleteDeals?: boolean; deleteLead?: boolean }) =>
      contactService.delete(id, { deleteDeals, deleteLead }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contactKeys.lists() });
      queryClient.invalidateQueries({ queryKey: dealKeys.all });
      queryClient.invalidateQueries({ queryKey: leadKeys.all });
      toast.success('Contact deleted successfully');
    },
    onError: () => toast.error('Failed to delete contact'),
  });

  return {
    contacts: contactsQuery.data?.data ?? [],
    meta: contactsQuery.data?.meta,
    isLoading: contactsQuery.isLoading,
    filters,
    createContact,
    updateContact,
    deleteContact,
    exportCsv: () => contactService.exportCsv(filters.current),
  };
}

export function useContact(id: string) {
  const queryClient = useQueryClient();

  const contactQuery = useQuery({
    queryKey: contactKeys.detail(id),
    queryFn: () => contactService.getById(id),
    enabled: !!id,
  });

  const activitiesQuery = useQuery({ queryKey: contactKeys.activities(id), queryFn: () => contactService.getActivities(id), enabled: !!id });
  const notesQuery = useQuery({ queryKey: contactKeys.notes(id), queryFn: () => contactService.getNotes(id), enabled: !!id });
  const dealsQuery = useQuery({ queryKey: contactKeys.deals(id), queryFn: () => contactService.getDeals(id), enabled: !!id });
  const emailsQuery = useQuery({ queryKey: contactKeys.emails(id), queryFn: () => contactService.getEmails(id), enabled: !!id });
  const tasksQuery = useQuery({ queryKey: contactKeys.tasks(id), queryFn: () => contactService.getTasks(id), enabled: !!id });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: contactKeys.detail(id) });
  };

  return {
    contact: contactQuery.data?.data,
    isLoading: contactQuery.isLoading,
    activities: activitiesQuery.data?.data ?? [],
    notes: notesQuery.data?.data ?? [],
    deals: dealsQuery.data?.data ?? [],
    emails: emailsQuery.data?.data ?? [],
    tasks: tasksQuery.data?.data ?? [],
    invalidate,
  };
}
